import CustomSnackbar from "../../../components/Feedback/CustomSnackbar";
import ReadOnlyProtectedForm from "../../../components/Inputs/ReadOnlyProtectedForm";
import TextInput from "../../../components/Inputs/TextInput";
import CustomSplitColumnCard from "../../../components/misc/CustomSplitColumnCard";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";
import getSaveErrorMessages from "../../../functions/general/get-save-error-messages";
import useServerValidationResponseEffect from "../../../hooks/useServerValidationResponseEffect";
import useValidatedInputEffect from "../../../hooks/useValidatedInputEffect";
import { validateRoomName } from "../../../validation/room-validation";
import { selectRooms, selectRoomsRestSet, selectSelectedRoom } from "../../../store/room/room-selectors";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { InfoIcon } from "../../../utils/icons/icons";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CustomInfoWithLabel from "../../misc/CustomInfoWithLabel";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ValidationResult from "../../../interfaces/api/validation-result";
import SoftwareOperatingMode from "../../../enums/robot/configuration/general/software-operating-mode";
import { selectSystemSettings } from "../../../store/robot-configurations/robot-configurations-selectors";

const GeneralMapConfigurations: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const { patchRoomsRestSet, patchRoomById } = useSharedActions();

  const room = useSharedSelector((state) => selectSelectedRoom(state));
  const roomRestSet = useSharedSelector((state) => selectRoomsRestSet(state));
  const availableRooms = useSharedSelector((state) => selectRooms(state));
  const systemSettings = useSharedSelector((state) => selectSystemSettings(state));

  const [saveLock, setSaveLock] = useState(false);

  const { resetToggle, removeServerError, getServerValidationResultOrNull } = useServerValidationResponseEffect({
    patchError: roomRestSet.patchError,
    resetPatchHandler: () => dispatch(patchRoomsRestSet({ patchStatus: HttpFetchStatus.Idle, patchError: null })),
  });

  const roomNameValidator = (value: string): ValidationResult => {
    const existingRoomNames = availableRooms.filter((r) => r.id !== room?.id).map((r) => r.name) ?? [];
    return validateRoomName(value, existingRoomNames);
  };

  const {
    value: name,
    validationResult: nameValidationResult,
    valueChangeHandler: nameChangeHandler,
  } = useValidatedInputEffect(
    room?.name ?? "",
    roomNameValidator,
    (_) => () => {},
    getServerValidationResultOrNull("name"),
    removeServerError,
    resetToggle
  );

  const {
    value: fleetControlId,
    validationResult: fleetControlIdValidationResult,
    valueChangeHandler: fleetControlIdChangeHandler,
  } = useValidatedInputEffect(
    room?.fleetControlId ?? "",
    (newId) => ({ isValid: true, error: null }),
    (_) => () => {},
    getServerValidationResultOrNull("fleetControlId"),
    removeServerError,
    resetToggle
  );

  const patchRoomNameHandler = () => {
    if (room != null && nameValidationResult.isValid) {
      setSaveLock(true);
      dispatch(patchRoomById({ id: room.id, data: { name } }));
    }
  };

  const patchRoomFleetControlIdHandler = () => {
    if (room != null) {
      setSaveLock(true);
      dispatch(patchRoomById({ id: room.id, data: { fleetControlId } }));
    }
  };

  const formSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    patchRoomNameHandler();
  };

  const snackbarCloseHandler = () => {
    setSaveLock(false);
    dispatch(patchRoomsRestSet({ patchStatus: HttpFetchStatus.Idle, patchError: null }));
  };

  return (
    <React.Fragment>
      <CustomSplitColumnCard
        title={t("room-details")}
        content={[
          {
            left: (
              <Stack spacing={1} pr={2}>
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <InfoIcon />
                  <Typography>{t("configuration-general")}</Typography>
                </Stack>
              </Stack>
            ),
            right: (
              <Stack spacing={2}>
                <ReadOnlyProtectedForm autoComplete="off" onSubmit={formSubmitHandler}>
                  <TextInput
                    label={t("room-configuration-roomname")}
                    onBlur={patchRoomNameHandler}
                    value={name}
                    onChange={nameChangeHandler}
                    validationResult={nameValidationResult}
                  />
                  {systemSettings.data?.softwareOperatingMode === SoftwareOperatingMode.FleetControl && (
                    <TextInput
                      label={t("fleet-control-id")}
                      onBlur={patchRoomFleetControlIdHandler}
                      value={fleetControlId}
                      onChange={fleetControlIdChangeHandler}
                      validationResult={fleetControlIdValidationResult}
                    />
                  )}
                </ReadOnlyProtectedForm>
                <CustomInfoWithLabel
                  label={t("room-configuration-mapping-station-nr")}
                  text={room?.primaryStation.stationId ?? ""}
                />
                {room?.secondaryStation != null && (
                  <CustomInfoWithLabel
                    label={t("room-configuration-mapping-station-nr")}
                    text={room.secondaryStation.stationId ?? ""}
                  />
                )}
              </Stack>
            ),
          },
        ]}
      />
      <CustomSnackbar
        title={t("error-saving-room")}
        open={saveLock && roomRestSet.patchStatus === HttpFetchStatus.Failed}
        onClose={snackbarCloseHandler}
        textMessages={getSaveErrorMessages(roomRestSet.patchError)}
        severity="error"
      />
      <CustomSnackbar
        title={t("success-saving-room")}
        severity="success"
        open={saveLock && roomRestSet.patchStatus === HttpFetchStatus.Success}
        onClose={snackbarCloseHandler}
      />
    </React.Fragment>
  );
};

export default GeneralMapConfigurations;
